import { isAxiosError } from 'axios';

import {
  AdminCaptureHttpMethod,
  enrichAdminCaptureScope,
} from './admin-request-capture-context';
import { adminCaptureException } from './sentry-capture-exception';

export const ADMIN_SENTRY_CAPTURED = Symbol.for('adminSentryCaptured');

export type AdminCaptureMeta = {
  requestPath: string;
  httpMethod: AdminCaptureHttpMethod;
  queryKey: string;
  operation: string;
  feature?: string;
  queryParams?: Record<string, unknown>;
};

export type CaptureAdminApiErrorOptions = AdminCaptureMeta & {
  error: unknown;
  extras?: Record<string, unknown>;
  user?: { id?: string; email?: string };
  level?: 'error' | 'warning' | 'info';
};

type ApiErrorBody = {
  message?: string;
  statusCode?: number;
  [key: string]: unknown;
};

type CapturableError = {
  [ADMIN_SENTRY_CAPTURED]?: boolean;
};

function getErrorPayload(error: unknown): {
  message: string;
  statusCode?: number;
  errorBody: unknown;
} {
  if (isAxiosError<ApiErrorBody>(error)) {
    const respData = error.response?.data;
    return {
      message:
        respData?.message ??
        (error instanceof Error ? error.message : 'Unknown error'),
      statusCode: respData?.statusCode ?? error.response?.status,
      errorBody: respData ?? error.toJSON?.() ?? error.message,
    };
  }

  return {
    message: error instanceof Error ? error.message : 'Unknown error',
    errorBody: error instanceof Error ? error.message : error,
  };
}

function stripUndefined(
  extras: Record<string, unknown> | undefined
): Record<string, unknown> {
  if (!extras) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(extras).filter(([, value]) => value !== undefined)
  );
}

export function isAdminSentryCaptured(error: unknown): boolean {
  if (!error || (typeof error !== 'object' && typeof error !== 'function')) {
    return false;
  }

  return Boolean((error as CapturableError)[ADMIN_SENTRY_CAPTURED]);
}

export function markAdminSentryCaptured(error: unknown): void {
  if (!error || (typeof error !== 'object' && typeof error !== 'function')) {
    return;
  }

  try {
    Object.defineProperty(error, ADMIN_SENTRY_CAPTURED, {
      value: true,
      configurable: true,
    });
  } catch {
    (error as CapturableError)[ADMIN_SENTRY_CAPTURED] = true;
  }
}

/**
 * Captures an admin API/React Query failure with shared request enrichment.
 * Marks the error so MutationCache safety nets can skip duplicate events.
 */
export function captureAdminApiError(
  options: CaptureAdminApiErrorOptions
): void {
  if (isAdminSentryCaptured(options.error)) {
    return;
  }

  const { message, statusCode, errorBody } = getErrorPayload(options.error);
  const cleanedExtras = stripUndefined(options.extras);

  adminCaptureException(
    {
      message,
      ...(statusCode !== undefined ? { statusCode } : {}),
    },
    (scope) => {
      enrichAdminCaptureScope(scope, {
        requestPath: options.requestPath,
        httpMethod: options.httpMethod,
        queryKey: options.queryKey,
        operation: options.operation,
        statusCode,
        errorBody,
        queryParams: options.queryParams,
      });

      if (options.feature) {
        scope.setTag('feature', options.feature);
      }
      if (options.user?.id || options.user?.email) {
        scope.setUser({
          id: options.user.id,
          email: options.user.email,
        });
      }
      if (Object.keys(cleanedExtras).length > 0) {
        scope.setExtra('admin_api', cleanedExtras);
      }
      if (options.level) {
        scope.setLevel(options.level);
      }
    }
  );

  markAdminSentryCaptured(options.error);
}
